import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useState } from 'react';
import './BigQueryRoute.css';
import { invoicesRows, invoicesQuery } from '../utils/exampleInvoices';
import { positionsRows, positionsQuery } from '../utils/examplePositions';
import { transactionsRows, transactionsQuery } from '../utils/exampleTransactions';
import { expensesRows, expensesQuery } from '../utils/exampleExpenses';

interface SnackMsg {
  type: 'success' | 'warning' | 'info' | 'error';
  message: string;
  isShown: boolean;
}

const sqlBlockSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  backgroundColor: 'action.hover',
  borderRadius: 1,
  padding: '12px 40px 12px 12px',
  whiteSpace: 'pre',
  overflowX: 'auto',
  margin: 0,
};

function SqlBlock({
  query,
  onCopy,
}: {
  query: string;
  onCopy: (text: string) => void;
}) {
  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="pre" sx={sqlBlockSx}>
        {query}
      </Box>
      <Tooltip title="Copy SQL">
        <IconButton
          size="small"
          onClick={() => onCopy(query)}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <ContentCopyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function PreviewTable<T extends Record<string, unknown>>({
  rows,
  idPrefix,
}: {
  rows: readonly T[];
  idPrefix: string;
}) {
  const columns = Object.keys(rows[0]);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table sx={{ minWidth: 650 }} size="small" aria-label={`${idPrefix} preview`}>
        <TableHead>
          <TableRow>
            <TableCell key={`${idPrefix}_header_row`}>Row</TableCell>
            {columns.map((c) => (
              <TableCell align="right" key={`${idPrefix}_header_${c}`}>
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ind) => (
            <TableRow
              key={ind}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row" key={`${idPrefix}_cell_row_${ind}`}>
                {ind}
              </TableCell>
              {columns.map((c) => (
                <TableCell align="right" key={`${idPrefix}_cell_${c}_${ind}`}>
                  {row[c] as React.ReactNode}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface QuerySectionProps<T extends Record<string, unknown>> {
  title: string;
  description: string;
  query: string;
  rows: readonly T[];
  idPrefix: string;
  onCopy: (text: string) => void;
}

function QuerySection<T extends Record<string, unknown>>({
  title,
  description,
  query,
  rows,
  idPrefix,
  onCopy,
}: QuerySectionProps<T>) {
  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack>
          <Typography fontWeight={600}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          <div>
            <Typography variant="caption" color="text.secondary">
              SQL
            </Typography>
            <SqlBlock query={query} onCopy={onCopy} />
          </div>
          <div>
            <Typography variant="caption" color="text.secondary">
              Sample result
            </Typography>
            <PreviewTable rows={rows} idPrefix={idPrefix} />
          </div>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function BigQueryRoute() {
  const [showMsg, setShowMsg] = useState<SnackMsg>({
    type: 'success',
    message: '',
    isShown: false,
  });

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowMsg({ type: 'success', message: 'SQL copied!', isShown: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      setShowMsg({ type: 'error', message, isShown: true });
    }
  };

  return (
    <div className="BigQueryRoute">
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        <AlertTitle>BigQuery datasets</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Run special table functions and SQL queries against the{' '}
          <Box
            component="code"
            sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
          >
            invoicemaker-f5e1d.mini_accountant
          </Box>{' '}
          dataset to access your own data.
        </Typography>
        <Typography variant="body2">
          You'll need a{' '}
          <Link href="https://console.cloud.google.com/welcome" target="_blank" rel="noopener noreferrer">
            Google Cloud Platform
          </Link>{' '}
          project tied to the same email you use in the application, plus the{' '}
          <Link
            href="https://cloud.google.com/bigquery/docs/access-control#bq-permissions"
            target="_blank"
            rel="noopener noreferrer"
          >
            bigquery.jobs.create
          </Link>{' '}
          permission. Authenticate with{' '}
          <Link
            href="https://developers.google.com/identity/protocols/oauth2"
            target="_blank"
            rel="noopener noreferrer"
          >
            OAuth2
          </Link>{' '}
          before issuing requests. See the{' '}
          <Link
            href="https://miniaccountant.app/storage/manual.pdf#page=10"
            target="_blank"
            rel="noopener noreferrer"
          >
            Looker integration guide
          </Link>{' '}
          for setup details. GCP usage is billed by Google according to your
          project's tariff (a free tier is available).
        </Typography>
      </Alert>

      <Stack spacing={1.5}>
        <QuerySection
          title="Invoices by date range"
          description="One row per invoice with payment totals in user currency."
          query={invoicesQuery}
          rows={invoicesRows}
          idPrefix="invoices"
          onCopy={copyToClipboard}
        />
        <QuerySection
          title="Positions by date range"
          description="Line-level breakdown of services across invoices."
          query={positionsQuery}
          rows={positionsRows}
          idPrefix="positions"
          onCopy={copyToClipboard}
        />
        <QuerySection
          title="Transactions by date range"
          description="Recorded payments per invoice with conversion details."
          query={transactionsQuery}
          rows={transactionsRows}
          idPrefix="transactions"
          onCopy={copyToClipboard}
        />
        <QuerySection
          title="Expenses by date range"
          description="One row per business expense with VAT and reverse-charge details."
          query={expensesQuery}
          rows={expensesRows}
          idPrefix="expenses"
          onCopy={copyToClipboard}
        />
      </Stack>

      <Snackbar
        open={showMsg.isShown}
        autoHideDuration={6000}
        onClose={() => setShowMsg({ ...showMsg, isShown: false })}
        sx={{ width: '100%', padding: '3vmin', justifyContent: 'end' }}
      >
        <Alert
          onClose={() => setShowMsg({ ...showMsg, isShown: false })}
          sx={{ marginRight: '3vmin' }}
          severity={showMsg.type}
        >
          {showMsg.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default BigQueryRoute;
